import { useRef, useState, useEffect } from 'react'
import { useCrosswordStore } from '../store'
import type { CrosswordData } from '../types'
import { dropdown, modal } from '../lib/Z'
import { hasMeaningfulCrosswordData, sanitizeCrosswordData } from '../lib/data'
import { buildPlacedPairsPlainText } from '../lib/plaintextExport'

export function ExportImport() {
  const { exportData, importData, mergeData, clearAllData, words, pairings, constructorGrid, listNames } = useCrosswordStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [pendingData, setPendingData] = useState<CrosswordData | null>(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleExport = () => {
    const data = exportData()
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `crossword-data-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
    setShowDropdown(false)
  }

  const handlePlaintextExport = () => {
    const text = buildPlacedPairsPlainText(constructorGrid, pairings, listNames)
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `crossword-pairs-${new Date().toISOString().split('T')[0]}.txt`
    a.click()
    URL.revokeObjectURL(url)
    setShowDropdown(false)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const parsed: unknown = JSON.parse(event.target?.result as string)
        const data = sanitizeCrosswordData(parsed)
        if (data && hasMeaningfulCrosswordData(data)) {
          setPendingData(data)
          setShowImportModal(true)
        } else {
          alert('Invalid data format - no crossword data found')
        }
      } catch {
        alert('Failed to parse JSON file')
      }
    }
    reader.readAsText(file)

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setShowDropdown(false)
  }

  const handleReplace = () => {
    if (pendingData) {
      importData(pendingData)
    }
    setPendingData(null)
    setShowImportModal(false)
  }

  const handleMerge = () => {
    if (pendingData) {
      mergeData(pendingData)
    }
    setPendingData(null)
    setShowImportModal(false)
  }

  const handleCancelImport = () => {
    setPendingData(null)
    setShowImportModal(false)
  }

  const handleClear = () => {
    if (confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      clearAllData()
    }
    setShowDropdown(false)
  }

  // Count what would be imported
  const pendingWordCount = pendingData?.words?.length || 0
  const pendingPairingCount = pendingData?.pairings?.length || 0
  const pendingDraftCount = pendingData?.drafts?.length || 0

  return (
    <>
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-400 tabular-nums">
          {words.length} words · {pairings.length} pairs
        </span>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-white rounded-lg transition-all"
            title="Options"
          >
            ⋯
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg py-1.5 border border-gray-100" style={{ zIndex: dropdown }}>
              <button
                onClick={handleExport}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Export JSON
              </button>
              <button
                onClick={handlePlaintextExport}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Export Pairs TXT
              </button>
              <label className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer block transition-colors">
                Import JSON
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
              <div className="my-1.5 mx-3 border-t border-gray-100" />
              <button
                onClick={handleClear}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center backdrop-blur-sm" style={{ zIndex: modal }}>
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold mb-2">Import Data</h2>
            <p className="text-sm text-gray-500 mb-5">
              File contains {pendingWordCount} words, {pendingPairingCount} pairings, and {pendingDraftCount} drafts.
              <br />
              You currently have {words.length} words and {pairings.length} pairings.
            </p>

            <div className="space-y-3">
              <button
                onClick={handleReplace}
                className="w-full px-4 py-3.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors text-left"
              >
                <span className="font-medium">Replace</span>
                <span className="block text-sm text-gray-400 mt-0.5">
                  Clear existing data and import the file
                </span>
              </button>

              <button
                onClick={handleMerge}
                className="w-full px-4 py-3.5 bg-gray-50 text-gray-900 rounded-xl hover:bg-gray-100 transition-colors text-left"
              >
                <span className="font-medium">Merge</span>
                <span className="block text-sm text-gray-500 mt-0.5">
                  Add new words and pairings, skip duplicates
                </span>
              </button>

              <button
                onClick={handleCancelImport}
                className="w-full px-4 py-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
